import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Paper,
  Tooltip,
  Chip,
  CircularProgress,
  alpha,
} from "@mui/material";
import dayjs from "dayjs";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useNavigate, useLocation } from "react-router-dom";
import {
  updatepos,
  ApproveSolistapi,
  DivisionList,
  GetApprovedSuppliers as fetchSuppliers,
  ApproveIndentlistapi as IndentListApi,
  postpos,
  GetNextPoNo,
  reserveMOSItemStock,
  releaseMOSItemStock,
  getCompanyProfile,
} from "../../../Services/Services";
import Autocomplete from "@mui/material/Autocomplete";
import { toast, ToastContainer } from "react-toastify";
import AddItemForm from "../Inventory/ItemForm";
import { debounce } from "lodash";

export default function PurchaseOrderForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [reservingIndex, setReservingIndex] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [indents, setIndents] = useState([]);
  const [indentLoading, setIndentLoading] = useState(false);
  const [indentSearch, setIndentSearch] = useState("");
  const [projects, setProjects] = useState([]);
  const location = useLocation();

  const { poData, isEdit } = location.state || {};
  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();
  // Default "Billed To" data (auto-filled from Company Profile)
  const [billedTo, setBilledTo] = useState({
    name: "",
    state_name: "",
    state_code: "",
    gst_no: "",
    pan_no: "",
    address: "",
    contact: "",
    email: "",
  });

  const [formData, setFormData] = useState({
    po_number: "",
    indent: "",
    supplier: "",
    division: storedDivision?.division_id || "",
    division_name: storedDivision?.division || "",
    project: "",
    billing_name: "",
    billing_address: "",
    billing_pan: "",
    billing_gst: "",
    billing_division: storedDivision?.division || "",
    billing_email: "",
    billing_contact: "",
    shipping_address: "",
    shipping_contact: "",
    shipping_phone: "",
    items: [
      {
        category: "",
        category_name: "",
        sub_category: "",
        sub_category_name: "",
        item: "",
        description: "",
        indent_qty: 0,
        quantity: 0,
        used_qty: 0,
        unit: "",
        unit_name: "",
        size: "",
        size_name: "",
        rate: "",
        amount: "",
      },
    ],
    created_by: localStorage.getItem("user_id"),
  });

  const navigate = useNavigate();
  const [openItemModal, setOpenItemModal] = useState(false);
  const [includeShipping, setIncludeShipping] = useState(false);

  const [terms, setTerms] = useState([
    { key: "Delivery Period", value: "Ready stock", is_default: true },
    { key: "Warranty", value: "12 months from delivery", is_default: true },
    { key: "Freight", value: "Ex works. Raipur", is_default: true },
    { key: "Inspection", value: "By buyer at site", is_default: true },
    { key: "Quality", value: "As per specifications", is_default: true },
    {
      key: "Rejection/Claim",
      value: "Allowed within 7 days",
      is_default: true,
    },
    { key: "Jurisdiction", value: "Raipur", is_default: true },
    {
      key: "Payment Terms",
      value: "Material received after 30 days",
      is_default: true,
    },
    { key: "GST", value: "18% applicable", is_default: true },
    { key: "Billing gst no-", value: storedDivision?.gst_no, is_default: true },
  ]);

  const totalValue = [...formData.items].reduce(
    (sum, i) => sum + (parseFloat(i.rate) || 0) * (parseFloat(i.quantity) || 0),
    0,
  );

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];

    if (field === "quantity") {
      const enteredQty = parseFloat(value || 0);
      const availableQty = parseFloat(updatedItems[index].main_indent_qty) || 0;
      const indentQty = parseFloat(updatedItems[index].indent_qty) || 0;
      const usedQty = parseFloat(updatedItems[index].used_qty) || 0;

      // Check against available quantity
      if (availableQty > 0 && enteredQty > availableQty) {
        toast.error(
          `Cannot exceed available quantity!\n` + `Available: ${availableQty}`,
        );
        return; // ✅ Exit before setting value
      }
      if (enteredQty < 0) {
        toast.error("Quantity cannot be negative");
        return;
      }
    }

    updatedItems[index][field] = value;

    // Calculate amount
    const qty = parseFloat(updatedItems[index].quantity) || 0;
    const rate = parseFloat(updatedItems[index].rate) || 0;
    updatedItems[index].amount = (qty * rate).toFixed(2);

    setFormData((prev) => ({
      ...prev,
      items: updatedItems,
    }));
  };

  const debouncedFetchIndents = useCallback(
    debounce(async (searchText) => {
      try {
        setIndentLoading(true);
        const filters = {
          ...(searchText && { indent_no: searchText }),
        };
        const divisionId = storedDivision?.division_id;
        if (divisionId) {
          filters.division = divisionId;
        }

        const response = await IndentListApi(1, filters);
        const results = Array.isArray(response.results)
          ? response.results
          : response || [];
        setIndents(results);
      } catch (err) {
        toast.error("Failed to load indents");
        setIndents([]);
      } finally {
        setIndentLoading(false);
      }
    }, 500),
    [],
  );

  const fetchIndents = async (divisionId) => {
    try {
      setIndentLoading(true);
      const filters = {};
      const finalDivisionId = divisionId || getSelectedDivision()?.division_id;
      if (finalDivisionId) {
        filters.division = finalDivisionId;
      }
      const response = await IndentListApi(1, filters);
      const results = Array.isArray(response.results)
        ? response.results
        : response || [];
      setIndents(results);
    } catch (err) {
      toast.error("Failed to load indents");
      setIndents([]);
    } finally {
      setIndentLoading(false);
    }
  };

  const handleIndentSearchChange = (searchText) => {
    setIndentSearch(searchText);
    debouncedFetchIndents(searchText);
  };

  const fetchAllProjects = async () => {
    const division = storedDivision?.division_id;
    try {
      const response = await ApproveSolistapi(1, division ? { division: division } : {});
      const results = Array.isArray(response.results)
        ? response.results
        : response || [];
      setProjects(results);
    } catch (err) {
      toast.error("Failed to load projects");
    }
  };

  const debouncedFetchSuppliers = useCallback(
    debounce(async (search) => {
      try {
        const response = await fetchSuppliers(1, { name: search });
        const results = Array.isArray(response.results)
          ? response.results
          : response || [];
        setSuppliers(results);
      } catch (err) {
        toast.error("Failed to load suppliers");
      }
    }, 500),
    [],
  );

  useEffect(() => {
    debouncedFetchSuppliers("");
  }, []);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        fetchAllProjects();
      } catch (error) {
        console.error("Error fetching dropdowns:", error);
      }
    };

    const divisionData = getSelectedDivision();

    if (isEdit && poData) {
      setFormData({
        ...formData,
        ...poData,
        created_by: localStorage.getItem("user_id"),
        billing_email: poData.billing_email || "",
        billing_contact: poData.billing_contact || "",
        division: divisionData?.division_id || poData.division || "",
        division_name: divisionData?.division || poData.division_name || "",
        items: (poData.items || []).map((item) => ({
          ...item,
          rate: parseFloat(item.rate) || 0,
          quantity: parseFloat(item.quantity) || 0,
          amount: (
            (parseFloat(item.rate) || 0) * (parseFloat(item.quantity) || 0)
          ).toFixed(2),
          size: item.size || "",
          size_name: item.size_name || item.size?.name || "",
        })),
      });
      setIncludeShipping(!!poData.shipping_address);
      setTerms(poData.terms_conditions || []);

      fetchIndents(divisionData?.division_id);

      if (poData.billed_to) {
        setBilledTo(poData.billed_to);
      }
    } else {
      fetchIndents(divisionData?.division_id);
    }

    fetchDropdowns();
    if (!isEdit && !poData?.po_id) {
      fetchNextPoNo();
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem("selectedDivision");
      if (stored) {
        try {
          const divisionData = JSON.parse(stored);
          setBilledTo((prev) => ({
            ...prev,
            gst_no: divisionData?.gst_no || prev.gst_no,
            pan_no: divisionData?.pan_no || prev.pan_no,
            address: divisionData?.address || prev.address,
          }));

          // Update terms GST
          setTerms((prev) =>
            prev.map((term) =>
              term.key === "Billing GST No"
                ? { ...term, value: divisionData?.gst_no || "" }
                : term,
            ),
          );
        } catch (e) {
          console.error("Failed to parse selectedDivision:", e);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const fetchNextPoNo = async () => {
    const data = await GetNextPoNo();
    if (data && data.po_number) {
      setFormData((prev) => ({
        ...prev,
        po_number: data.po_number,
      }));
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleBilledToChange = (field) => (e) => {
    setBilledTo({ ...billedTo, [field]: e.target.value });
  };

  const handleTermChange = (index, field, value) => {
    const updatedTerms = [...terms];
    updatedTerms[index][field] = value;
    setTerms(updatedTerms);
  };

  const [errors, setErrors] = useState({
    billing_pan: "",
    billing_gst: "",
  });

  const validateField = (field, value) => {
    let error = "";

    if (field === "billing_pan") {
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
      if (value && !panRegex.test(value.toUpperCase())) {
        error = "Invalid PAN format (eg. AAAPZ1234C)";
      }
    }

    if (field === "billing_gst") {
      const gstRegex =
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (value && !gstRegex.test(value.toUpperCase())) {
        error = "Invalid GSTIN format (eg. 27ABCDE1234F1Z5)";
      }
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const handleCloseItemModal = async () => {
    setOpenItemModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const divisionData = getSelectedDivision();
    const finalDivisionId = divisionData?.division_id || formData.division || null;
    const finalDivisionName = divisionData?.division || formData.division_name || "";

    const newErrors = {};

    if (!formData.po_number) newErrors.po_number = "PO Number is required";
    if (!formData.supplier) newErrors.supplier = "Supplier is required";
    if (!formData.project) newErrors.project = "Project is required";
    if (!formData.indent) newErrors.indent = "Indent is required";
    if (!formData.billing_name)
      newErrors.billing_name = "Supplier Name is required";
    if (!formData.billing_address)
      newErrors.billing_address = "Supplier Address is required";

    formData.items.forEach((item, index) => {
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        newErrors[`items.${index}.quantity`] = "Valid quantity is required";
      }
      if (!item.rate || parseFloat(item.rate) < 0) {
        newErrors[`items.${index}.rate`] = "Valid rate is required";
      }
    });

    const panError = validateField("billing_pan", formData.billing_pan);
    if (panError) newErrors.billing_pan = panError;

    const gstError = validateField("billing_gst", formData.billing_gst);
    if (gstError) newErrors.billing_gst = gstError;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fix all validation errors before submitting");

      setTimeout(() => {
        const firstErrorElement =
          document.querySelector(".Mui-error") ||
          document.querySelector('input[style*="border: 1px solid red"]');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      return;
    }

    setErrors({});

    const filteredTerms = terms.filter(
      (term) => term.key?.trim() !== "" && term.value?.trim() !== "",
    );

    const cleanedItems = formData.items.map((item) => ({
      ...item,
      category: item.category || null,
      category_name: item.category_name || "",
      sub_category: item.sub_category || null,
      sub_category_name: item.sub_category_name || "",
      item: item.item || null,
      unit_name: item.unit_name || "",
      size: item.size || null,
      size_name: item.size_name || "",
      quantity: parseFloat(item.quantity) || 0,
      rate: parseFloat(item.rate) || 0,
      amount: parseFloat(item.amount) || 0,
      indent_qty: parseFloat(item.indent_qty) || 0,
      used_qty: parseFloat(item.used_qty) || 0,
    }));

    const payload = {
      indent: formData.indent,
      supplier: formData.supplier,
      project: formData.project,
      division: finalDivisionId,
      billing_name: formData.billing_name,
      billing_address: formData.billing_address,
      billing_pan: formData.billing_pan,
      billing_gst: formData.billing_gst,
      billing_division: finalDivisionName,
      billing_email: formData.billing_email,
      billing_contact: formData.billing_contact,

      billed_to_name: billedTo.name || "",
      billed_to_address: billedTo.address || "",
      billed_to_state_name: billedTo.state_name || "",
      billed_to_state_code: billedTo.state_code || "",
      billed_to_gst_no: billedTo.gst_no || "",
      billed_to_pan_no: billedTo.pan_no || "",
      billed_to_contact: billedTo.contact || "",
      billed_to_email: billedTo.email || "",

      shipping_address: formData.shipping_address,
      shipping_contact: formData.shipping_contact,
      shipping_phone: formData.shipping_phone,
      items: cleanedItems,
      include_shipping: includeShipping,
      terms_conditions: filteredTerms,
      created_by: localStorage.getItem("user_id"),
      quotation_date: formData.quotation_date
        ? dayjs(formData.quotation_date).format("YYYY-MM-DD")
        : null,
    };

    if (!isEdit && !poData?.po_id) {
      payload.po_number = formData.po_number;
    }

    console.log("Payload being sent:", JSON.stringify(payload, null, 2));

    try {
      if (isEdit && poData?.po_id) {
        // ✅ For edit, use PATCH/PUT without po_number
        await updatepos(poData.po_id, payload);
        toast.success("Purchase Order updated successfully");
      } else {
        // ✅ For create, include po_number
        await postpos(payload);
        toast.success("Purchase Order created successfully");
      }

      sessionStorage.removeItem("poDraft");
      sessionStorage.removeItem("poTerms");
      sessionStorage.removeItem("billedTo");

      setTimeout(() => {
        navigate("/dashboard/po-list");
      }, 1500);

      if (!isEdit) {
        setFormData({
          po_number: "",
          indent: "",
          supplier: "",
          division: "",
          project: "",
          billing_name: "",
          billing_address: "",
          billing_pan: "",
          billing_gst: "",
          billing_division: "",
          shipping_address: "",
          shipping_contact: "",
          shipping_phone: "",
          billing_email: "",
          billing_contact: "",
          items: [createEmptyItem()],
          created_by: localStorage.getItem("user_id"),
        });
        setTerms([]);
        setIncludeShipping(false);
        setBilledTo({
          name: "CRANOIST MHE Pvt. Ltd.",
          address: storedDivision?.address,
          state_name: "Chhattisgarh",
          state_code: "22",
          gst_no: storedDivision?.gst_no,
          pan_no: storedDivision?.pan_no,
          contact: "+91 9825586999 ",
          email: "info@cranoistmhe.com",
        });
      }
    } catch (err) {
      console.error("Submit error:", err);

      // ✅ Better error handling
      if (err.response?.data) {
        const errorData = err.response.data;

        // Handle specific field errors
        if (errorData.po_number) {
          toast.error(`PO Number Error: ${errorData.po_number}`);
        } else if (errorData.detail) {
          toast.error(errorData.detail);
        } else if (errorData.error) {
          toast.error(errorData.error);
        } else {
          toast.error("Failed to save Purchase Order");
        }
      } else {
        toast.error("Failed to save Purchase Order");
      }
    }
  };

  const removeTerm = (index) => {
    const updatedTerms = terms.filter((_, i) => i !== index);
    setTerms(updatedTerms);
  };

  const inputStyle = {
    width: "100%",
    padding: "8px",
    fontSize: "0.875rem",
    backgroundColor: "#ffffff",
    border: "1px solid #c4c4c4",
    borderRadius: "4px",
    boxSizing: "border-box",
  };

  const handleSaveDraft = () => {
    sessionStorage.setItem("poDraft", JSON.stringify(formData));
    sessionStorage.setItem("poTerms", JSON.stringify(terms));
    sessionStorage.setItem("billedTo", JSON.stringify(billedTo));
    toast.info("Purchase Order draft saved!");
  };

  useEffect(() => {
    const draftForm = sessionStorage.getItem("poDraft");
    const draftTerms = sessionStorage.getItem("poTerms");
    const draftBilledTo = sessionStorage.getItem("billedTo");
    if (draftForm) {
      try {
        setFormData(JSON.parse(draftForm));
        if (draftTerms) setTerms(JSON.parse(draftTerms));
        if (draftBilledTo) setBilledTo(JSON.parse(draftBilledTo));
        toast.info("Loaded Purchase Order draft");
      } catch (e) {
        console.error("Error loading draft:", e);
      }
    }
  }, []);

  const handleInlineReserve = async (index) => {
    const item = formData.items[index];
    const qtyToReserve = parseFloat(item.reserve_input_qty);

    if (!qtyToReserve || qtyToReserve <= 0) {
      toast.warning("Please enter a valid reservation quantity > 0");
      return;
    }

    const availableFree = parseFloat(item.free_stock || 0);
    if (qtyToReserve > availableFree) {
      toast.error(
        `Cannot reserve more than available free stock (${availableFree})`
      );
      return;
    }

    const payload = item.mos_line_id
      ? { line_id: item.mos_line_id, qty: qtyToReserve }
      : { item_id: item.item, mos_id: formData.project, qty: qtyToReserve };

    if (!item.mos_line_id && !formData.project) {
      toast.warning("No linked MOS / Project found for this item reservation.");
      return;
    }

    setReservingIndex(index);
    try {
      const res = await reserveMOSItemStock(payload);
      toast.success(
        res.message || `Reserved ${qtyToReserve} units successfully for MOS!`
      );

      const updatedItems = [...formData.items];
      const newReserved =
        res.reserved_qty !== undefined
          ? res.reserved_qty
          : (parseFloat(item.mos_reserved_qty) || 0) + qtyToReserve;
      const newFree =
        res.free_stock !== undefined
          ? res.free_stock
          : Math.max(0, availableFree - qtyToReserve);
      const newReserveStock =
        res.reserve_stock !== undefined
          ? res.reserve_stock
          : (parseFloat(item.reserve_stock) || 0) + qtyToReserve;

      // Dynamically adjust PO quantity: reduce by reserved amount (min 0)
      const currentPoQty = parseFloat(item.quantity) || 0;
      const newPoQty = Math.max(0, currentPoQty - qtyToReserve);
      const rate = parseFloat(item.rate) || 0;
      const newAmount = parseFloat((newPoQty * rate).toFixed(2));

      updatedItems[index] = {
        ...item,
        mos_reserved_qty: newReserved,
        free_stock: newFree,
        reserve_stock: newReserveStock,
        mos_line_id: res.line_id || item.mos_line_id,
        quantity: newPoQty,
        amount: newAmount,
        reserve_input_qty: "",
      };

      setFormData((prev) => ({
        ...prev,
        items: updatedItems,
      }));
    } catch (err) {
      console.error("Error reserving stock:", err);
      toast.error(err.response?.data?.error || "Failed to reserve stock");
    } finally {
      setReservingIndex(null);
    }
  };

  const handleInlineRelease = async (index) => {
    const item = formData.items[index];
    const qtyToRelease =
      parseFloat(item.reserve_input_qty) ||
      parseFloat(item.mos_reserved_qty) ||
      0;

    if (qtyToRelease <= 0) {
      toast.warning("Please enter a valid quantity to release > 0");
      return;
    }

    const currentReserved = parseFloat(item.mos_reserved_qty) || 0;
    if (qtyToRelease > currentReserved) {
      toast.error(
        `Cannot release more than reserved quantity (${currentReserved})`
      );
      return;
    }

    const payload = item.mos_line_id
      ? { line_id: item.mos_line_id, qty: qtyToRelease }
      : { item_id: item.item, mos_id: formData.project, qty: qtyToRelease };

    setReservingIndex(index);
    try {
      const res = await releaseMOSItemStock(payload);
      toast.info(res.message || `Released ${qtyToRelease} reserved units.`);

      const updatedItems = [...formData.items];
      const newReserved =
        res.reserved_qty !== undefined
          ? res.reserved_qty
          : Math.max(0, currentReserved - qtyToRelease);
      const newFree =
        res.free_stock !== undefined
          ? res.free_stock
          : (parseFloat(item.free_stock) || 0) + qtyToRelease;
      const newReserveStock =
        res.reserve_stock !== undefined
          ? res.reserve_stock
          : Math.max(0, (parseFloat(item.reserve_stock) || 0) - qtyToRelease);

      // Dynamically adjust PO quantity: increase back by released amount
      const currentPoQty = parseFloat(item.quantity) || 0;
      const newPoQty = currentPoQty + qtyToRelease;
      const rate = parseFloat(item.rate) || 0;
      const newAmount = parseFloat((newPoQty * rate).toFixed(2));

      updatedItems[index] = {
        ...item,
        mos_reserved_qty: newReserved,
        free_stock: newFree,
        reserve_stock: newReserveStock,
        quantity: newPoQty,
        amount: newAmount,
        reserve_input_qty: "",
      };

      setFormData((prev) => ({
        ...prev,
        items: updatedItems,
      }));
    } catch (err) {
      console.error("Error releasing stock:", err);
      toast.error(err.response?.data?.error || "Failed to release stock");
    } finally {
      setReservingIndex(null);
    }
  };

  const handleIndentChange = (indentId) => {
    const selectedIndent = indents.find(
      (ind) => String(ind.indent_id) === String(indentId),
    );

    if (selectedIndent) {
      const selectedProject = projects.find(
        (proj) => String(proj.id) === String(selectedIndent.project),
      );

      const processedItems = (selectedIndent.items || []).map((item) => {
        const sizeId = item.size_id || item.size?.id || item.size || "";
        const sizeName = item.size_name || item.size?.name || item.size || "";

        const indentQty = parseFloat(item.indent_qty) || 0;
        const usedQty = parseFloat(item.used_qty) || 0;
        const mainIndentQty =
          item.main_indent_qty !== undefined
            ? parseFloat(item.main_indent_qty)
            : indentQty - usedQty;

        const currentStock = parseFloat(item.current_stock) || 0;
        const reserveStock = parseFloat(item.reserve_stock) || 0;
        const freeStock =
          item.free_stock !== undefined
            ? parseFloat(item.free_stock)
            : Math.max(0, currentStock - reserveStock);
        const mosReservedQty = parseFloat(item.mos_reserved_qty) || 0;
        const mosLineId = item.mos_line_id || null;

        const initialQuantity = mainIndentQty > 0 ? mainIndentQty : indentQty;
        const initialRate = parseFloat(item.estimated_rate) || 0;

        return {
          category: item.category || "",
          category_name: item.category_name || item.category?.name || "",
          sub_category: item.sub_category || "",
          sub_category_name:
            item.sub_category_name || item.sub_category?.name || "",
          item: item.item || "",
          item_name: item.item_name || item.item?.name || "",
          description: item.item_description || item.item_name || "",
          indent_qty: indentQty,
          used_qty: usedQty,
          main_indent_qty: mainIndentQty,
          current_stock: currentStock,
          reserve_stock: reserveStock,
          free_stock: freeStock,
          mos_reserved_qty: mosReservedQty,
          mos_line_id: mosLineId,
          reserve_input_qty: "",
          quantity: initialQuantity,
          unit: item.uom || "",
          unit_name: item.uom_name || item.uom || "",
          size: sizeId,
          size_name: sizeName,
          rate: initialRate,
          amount: parseFloat((initialQuantity * initialRate).toFixed(2)),
        };
      });

      setFormData((prev) => ({
        ...prev,
        indent: indentId,
        project: selectedIndent.project || "",
        division: selectedIndent.division || prev.division,
        items: processedItems,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        indent: "",
        project: "",
        items: [createEmptyItem()],
      }));
    }
  };

  const createEmptyItem = () => ({
    category: "",
    category_name: "",
    sub_category: "",
    sub_category_name: "",
    item: "",
    description: "",
    indent_qty: 0,
    quantity: 0,
    used_qty: 0,
    main_indent_qty: 0,
    current_stock: 0,
    reserve_stock: 0,
    free_stock: 0,
    mos_reserved_qty: 0,
    mos_line_id: null,
    reserve_input_qty: "",
    unit: "",
    unit_name: "",
    size: "",
    size_name: "",
    rate: "",
    amount: "",
  });

  return (
    <Box sx={{ px: 0, pt: 0, mt:0, maxWidth: 1200, mx: "auto" }}>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {/* ✅ BUTTONS */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 2 }}>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          onClick={handleSaveDraft}
          sx={{
            borderRadius: 0,
            height: 30,
            fontSize: "0.7rem",
            color: "#ff8400ff",
            borderColor: "#ff8400ff",
            minWidth: isMobile ? "auto" : 120,
            px: isMobile ? 2 : 3,
          }}
        >
          Draft
        </Button>
        <Button
          type="submit"
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={{
            height: 30,
            fontSize: "0.7rem",
            borderRadius: 0,
            minWidth: isMobile ? "auto" : 200,
            px: isMobile ? 2 : 3,
          }}
        >
          Submit Purchase Order
        </Button>
      </Box>

      {/* Main Fields Card */}
      <Box
        border="1px solid #ccc"
        borderRadius="8px"
        sx={{ mb: 3, p: 2 }}
        autoComplete="off"
      >
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={3} md={3}>
            <TextField
              fullWidth
              label="PO Number"
              required
              value={formData.po_number || ""}
              onChange={handleChange("po_number")}
              error={!!errors.po_number}
              helperText={errors.po_number}
              disabled
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          {/* ✅ Division - Read-only from localStorage */}
          {/* Division (Optional) */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Division"
              value={
                formData.division_name ||
                storedDivision?.division ||
                ""
              }
              disabled
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={indents}
              required
              getOptionLabel={(option) =>
                option.indent_no || `Indent ${option.indent_id}`
              }
              loading={indentLoading}
              isOptionEqualToValue={(option, value) =>
                option.indent_id === value?.indent_id
              }
              value={
                indents.find((i) => i.indent_id === formData.indent) || null
              }
              onChange={(e, newValue) => {
                if (newValue) {
                  handleIndentChange(newValue.indent_id);
                } else {
                  handleIndentChange("");
                }
              }}
              onInputChange={(e, newInputValue) => {
                handleIndentSearchChange(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Indent"
                  fullWidth
                  required
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      height: 45,
                      fontSize: "0.875rem",
                    },
                  }}
                  error={!!errors.indent}
                  helperText={errors.indent}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={projects}
              required
              getOptionLabel={(option) =>
                option.project_name || `Project ${option.project_name}`
              }
              value={projects.find((i) => i.id === formData.project) || null}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onChange={(e, newValue) => {
                if (newValue) {
                  setFormData((prev) => ({
                    ...prev,
                    project: newValue.id,
                    division: newValue.division || "",
                  }));
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    project: "",
                    division: "",
                  }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  fullWidth
                  required
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      height: 45,
                      fontSize: "0.875rem",
                    },
                  }}
                  error={!!errors.project}
                  helperText={errors.project}
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Billed To Card */}
      <Box border="1px solid #ccc" borderRadius="8px" sx={{ mb: 3, p: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
        >
          Billed To
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Company Name"
              value={billedTo.name}
              onChange={handleBilledToChange("name")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Contact Details"
              value={billedTo.contact}
              onChange={handleBilledToChange("contact")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={billedTo.email}
              onChange={handleBilledToChange("email")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={1.5}>
            <TextField
              fullWidth
              label="State Name"
              value={billedTo.state_name}
              onChange={handleBilledToChange("state_name")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={1.5}>
            <TextField
              fullWidth
              label="State Code"
              value={billedTo.state_code}
              onChange={handleBilledToChange("state_code")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="GST No"
              value={billedTo.gst_no}
              onChange={handleBilledToChange("gst_no")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="PAN No"
              value={billedTo.pan_no}
              onChange={handleBilledToChange("pan_no")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Address"
              value={billedTo.address}
              onChange={handleBilledToChange("address")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Billed From Card (Supplier Details) */}
      <Box border="1px solid #ccc" borderRadius="8px" sx={{ mb: 3, p: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
        >
          Billed From
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={suppliers}
              required
              getOptionLabel={(option) => option.name || ""}
              value={suppliers.find((s) => s.id === formData.supplier) || null}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onChange={(e, newValue) => {
                if (newValue) {
                  setFormData((prev) => ({
                    ...prev,
                    supplier: newValue.id,
                    billing_name: newValue.name || "",
                    billing_address: newValue.address || "",
                    billing_pan: newValue.pan || "",
                    billing_gst: newValue.gst || "",
                    billing_email: newValue.email || "",
                    billing_contact: newValue.contact || "",
                  }));
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    supplier: "",
                    billing_name: "",
                    billing_address: "",
                    billing_pan: "",
                    billing_gst: "",
                    billing_email: "",
                    billing_contact: "",
                  }));
                }
              }}
              onInputChange={(e, newInputValue) => {
                setSupplierSearch(newInputValue);
                debouncedFetchSuppliers(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supplier"
                  fullWidth
                  required
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      height: 45,
                      fontSize: "0.875rem",
                    },
                  }}
                  error={!!errors.supplier}
                  helperText={errors.supplier}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Supplier Name"
              required
              value={formData.billing_name || ""}
              onChange={handleChange("billing_name")}
              error={!!errors.billing_name}
              helperText={errors.billing_name}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.billing_email || ""}
              onChange={handleChange("billing_email")}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          {/* ✅ Add Contact Field */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Contact"
              value={formData.billing_contact || ""}
              onChange={handleChange("billing_contact")}
              inputProps={{ maxLength: 10 }}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Supplier Address"
              required
              value={formData.billing_address || ""}
              onChange={handleChange("billing_address")}
              error={!!errors.billing_address}
              helperText={errors.billing_address}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="PAN"
              value={formData.billing_pan || ""}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                setFormData({ ...formData, billing_pan: value });
                validateField("billing_pan", value);
              }}
              error={!!errors.billing_pan}
              helperText={errors.billing_pan}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="GST"
              value={formData.billing_gst || ""}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                setFormData({ ...formData, billing_gst: value });
                validateField("billing_gst", value);
              }}
              error={!!errors.billing_gst}
              helperText={errors.billing_gst}
              InputProps={{
                sx: {
                  height: 45,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Shipping Section */}
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeShipping}
              onChange={(e) => setIncludeShipping(e.target.checked)}
            />
          }
          label="Include Shipping Details"
        />
        {includeShipping && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Shipping Address"
                value={formData.shipping_address || ""}
                onChange={handleChange("shipping_address")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Contact Person"
                value={formData.shipping_contact || ""}
                onChange={handleChange("shipping_contact")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.shipping_phone || ""}
                onChange={handleChange("shipping_phone")}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
          </Grid>
        )}
      </Box>

      {/* TABLE SECTION */}
      {isMobile ? (
        // Mobile Card Layout
        <Box sx={{ mb: 3 }}>
          {(formData.items || []).map((item, index) => (
            <Card key={index} sx={{ mb: 2, border: "1px solid #ddd" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Item #{index + 1}
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Category"
                    value={item.category_name}
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Sub Category"
                    value={item.sub_category_name}
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Item"
                    value={item.item_name}
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Description"
                    value={item.description}
                    onChange={(e) =>
                      handleItemChange(index, "description", e.target.value)
                    }
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="Size"
                    value={item.size_name}
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Indent Qty"
                    type="number"
                    value={item.indent_qty}
                    onChange={(e) =>
                      handleItemChange(index, "indent_qty", e.target.value)
                    }
                    size="small"
                    disabled
                  />

                  {/* Stock & Reservation Breakdown */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.background.paper, 0.6)
                          : "#f9f9f9",
                      border: "1px solid #e0e0e0",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Free Warehouse Stock:
                      </Typography>
                      <Chip
                        size="small"
                        label={`${item.free_stock || 0}`}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          height: 22,
                          bgcolor:
                            (item.free_stock || 0) > 0
                              ? alpha("#2e7d32", 0.12)
                              : alpha("#d32f2f", 0.08),
                          color:
                            (item.free_stock || 0) > 0 ? "#2e7d32" : "#d32f2f",
                          border: `1px solid ${
                            (item.free_stock || 0) > 0
                              ? alpha("#2e7d32", 0.3)
                              : alpha("#d32f2f", 0.2)
                          }`,
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Reserved for MOS:
                      </Typography>
                      <Chip
                        icon={<LockIcon sx={{ fontSize: "12px !important" }} />}
                        size="small"
                        label={`${item.mos_reserved_qty || 0}`}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          height: 22,
                          bgcolor:
                            (item.mos_reserved_qty || 0) > 0
                              ? alpha("#7b1fa2", 0.12)
                              : alpha("#757575", 0.08),
                          color:
                            (item.mos_reserved_qty || 0) > 0
                              ? "#7b1fa2"
                              : "#757575",
                          border: `1px solid ${
                            (item.mos_reserved_qty || 0) > 0
                              ? alpha("#7b1fa2", 0.3)
                              : alpha("#757575", 0.2)
                          }`,
                        }}
                      />
                    </Box>

                    {/* Inline Quick Reserve / Release Controls */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Reserve Qty"
                        value={
                          item.reserve_input_qty !== undefined
                            ? item.reserve_input_qty
                            : ""
                        }
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "reserve_input_qty",
                            e.target.value
                          )
                        }
                        inputProps={{ min: 0, max: item.free_stock || 0 }}
                        sx={{ flex: 1 }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={
                          reservingIndex === index || (item.free_stock || 0) <= 0
                        }
                        onClick={() => handleInlineReserve(index)}
                        startIcon={
                          reservingIndex === index ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : (
                            <LockIcon sx={{ fontSize: 13 }} />
                          )
                        }
                        sx={{ textTransform: "none", height: 36 }}
                      >
                        Reserve
                      </Button>
                      {(item.mos_reserved_qty || 0) > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          disabled={reservingIndex === index}
                          onClick={() => handleInlineRelease(index)}
                          sx={{ textTransform: "none", height: 36 }}
                        >
                          Release
                        </Button>
                      )}
                    </Box>
                  </Box>

                  <TextField
                    fullWidth
                    label="PO Qty (To Order)"
                    type="number"
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemChange(index, "quantity", e.target.value)
                    }
                    size="small"
                    error={!!errors[`items.${index}.quantity`]}
                    helperText={errors[`items.${index}.quantity`]}
                  />

                  <TextField
                    fullWidth
                    label="Used Qty"
                    type="number"
                    value={item.used_qty}
                    onChange={(e) =>
                      handleItemChange(index, "used_qty", e.target.value)
                    }
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Unit"
                    value={item.unit_name}
                    size="small"
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Unit Price"
                    type="number"
                    value={item.rate}
                    onChange={(e) =>
                      handleItemChange(index, "rate", e.target.value)
                    }
                    size="small"
                    error={!!errors[`items.${index}.rate`]}
                    helperText={errors[`items.${index}.rate`]}
                  />

                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={item.amount}
                    onChange={(e) =>
                      handleItemChange(index, "amount", e.target.value)
                    }
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="Total"
                    value={(
                      parseFloat(item.rate || 0) *
                      parseFloat(item.quantity || 0)
                    ).toFixed(2)}
                    size="small"
                    disabled
                  />

                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        items: prev.items.filter((_, i) => i !== index),
                      }))
                    }
                    startIcon={<DeleteIcon />}
                  >
                    Delete Item
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}

          <Typography variant="h6" align="right" sx={{ mt: 2 }}>
            Grand Total: ₹ {totalValue.toFixed(2)}
          </Typography>
        </Box>
      ) : (
        // Desktop Table
        <TableContainer sx={{ mt: 2 }}>
          <Box
            border="1px solid #ccc"
            borderRadius="8px"
            sx={{ mb: 3, p: 2, minWidth: 2000 }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      backgroundColor: "#f5f5f5",
                      fontSize: "0.78rem",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>S.No</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Sub Category</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>UOM</TableCell>
                  <TableCell align="center">Indent Qty</TableCell>
                  <TableCell align="center">Free Stock</TableCell>
                  <TableCell align="center">MOS Reserved</TableCell>
                  <TableCell align="center">Quick Reserve / Release</TableCell>
                  <TableCell align="center">PO Qty</TableCell>
                  <TableCell align="center">Used Qty</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(formData.items || []).map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: "#fff !important",
                      "&:hover": {
                        backgroundColor: "#fff !important",
                      },
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <TextField
                        value={item.category_name}
                        size="small"
                        sx={{
                          minWidth: "50px",
                          backgroundColor: "#ffffffff",
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                        }}
                        disabled
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.sub_category_name}
                        size="small"
                        sx={{
                          minWidth: "50px",
                          backgroundColor: "#ffffff",
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                        }}
                        disabled
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.item_name}
                        size="small"
                        sx={{
                          minWidth: "50px",
                          backgroundColor: "#ffffff",
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                        }}
                        disabled
                      />
                    </TableCell>

                    <TableCell>
                      <TextField
                        value={item.size_name}
                        size="small"
                        sx={{
                          minWidth: "50px",
                          backgroundColor: "#ffffff",
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                        }}
                        disabled
                      />
                    </TableCell>

                    <TableCell>
                      <TextField
                        value={item.unit_name}
                        size="small"
                        sx={{
                          minWidth: "50px",
                          backgroundColor: "#ffffff",
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                        }}
                        disabled
                      />
                    </TableCell>

                    {/* Indent Qty */}
                    <TableCell align="center">
                      <input
                        type="number"
                        value={item.indent_qty}
                        style={{ ...inputStyle, textAlign: "center" }}
                        disabled
                      />
                    </TableCell>

                    {/* Free Stock in Warehouse */}
                    <TableCell align="center">
                      <Tooltip
                        title={`Current Physical: ${
                          item.current_stock || 0
                        } | Total Reserved: ${item.reserve_stock || 0}`}
                      >
                        <Chip
                          size="small"
                          label={`${item.free_stock || 0}`}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            height: 24,
                            bgcolor:
                              (item.free_stock || 0) > 0
                                ? alpha("#2e7d32", 0.12)
                                : alpha("#d32f2f", 0.08),
                            color:
                              (item.free_stock || 0) > 0
                                ? "#2e7d32"
                                : "#d32f2f",
                            border: `1px solid ${
                              (item.free_stock || 0) > 0
                                ? alpha("#2e7d32", 0.3)
                                : alpha("#d32f2f", 0.2)
                            }`,
                          }}
                        />
                      </Tooltip>
                    </TableCell>

                    {/* MOS Reserved Qty */}
                    <TableCell align="center">
                      <Tooltip
                        title={`Stock allocated for this MOS: ${
                          item.mos_reserved_qty || 0
                        }`}
                      >
                        <Chip
                          icon={
                            <LockIcon sx={{ fontSize: "12px !important" }} />
                          }
                          size="small"
                          label={`${item.mos_reserved_qty || 0}`}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            height: 24,
                            bgcolor:
                              (item.mos_reserved_qty || 0) > 0
                                ? alpha("#7b1fa2", 0.12)
                                : alpha("#757575", 0.08),
                            color:
                              (item.mos_reserved_qty || 0) > 0
                                ? "#7b1fa2"
                                : "#757575",
                            border: `1px solid ${
                              (item.mos_reserved_qty || 0) > 0
                                ? alpha("#7b1fa2", 0.3)
                                : alpha("#757575", 0.2)
                            }`,
                          }}
                        />
                      </Tooltip>
                    </TableCell>

                    {/* Quick Inline Reserve / Release */}
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        <input
                          type="number"
                          placeholder="Qty"
                          value={
                            item.reserve_input_qty !== undefined
                              ? item.reserve_input_qty
                              : ""
                          }
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "reserve_input_qty",
                              e.target.value
                            )
                          }
                          style={{
                            ...inputStyle,
                            width: "55px",
                            height: "28px",
                            textAlign: "center",
                            fontSize: "0.75rem",
                            padding: "2px 4px",
                          }}
                          min="0"
                          max={item.free_stock || 0}
                        />
                        <Tooltip
                          title={`Reserve free warehouse stock for this MOS (Available: ${
                            item.free_stock || 0
                          })`}
                        >
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={
                                reservingIndex === index ||
                                (item.free_stock || 0) <= 0
                              }
                              onClick={() => handleInlineReserve(index)}
                              sx={{
                                minWidth: "auto",
                                px: 1,
                                py: 0.2,
                                height: "28px",
                                fontSize: "0.7rem",
                                textTransform: "none",
                                boxShadow: "none",
                              }}
                              startIcon={
                                reservingIndex === index ? (
                                  <CircularProgress
                                    size={12}
                                    color="inherit"
                                  />
                                ) : (
                                  <LockIcon sx={{ fontSize: 13 }} />
                                )
                              }
                            >
                              Reserve
                            </Button>
                          </span>
                        </Tooltip>
                        {(item.mos_reserved_qty || 0) > 0 && (
                          <Tooltip
                            title={`Release reserved stock back to free pool (Reserved: ${
                              item.mos_reserved_qty || 0
                            })`}
                          >
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                disabled={reservingIndex === index}
                                onClick={() => handleInlineRelease(index)}
                                sx={{
                                  minWidth: "auto",
                                  px: 0.8,
                                  py: 0.2,
                                  height: "28px",
                                  fontSize: "0.7rem",
                                  textTransform: "none",
                                }}
                              >
                                Release
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>

                    {/* PO Qty (Editable) */}
                    <TableCell align="center">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(index, "quantity", e.target.value)
                        }
                        style={{
                          ...inputStyle,
                          textAlign: "center",
                          border: errors[`items.${index}.quantity`]
                            ? "1px solid red"
                            : inputStyle.border,
                        }}
                      />
                      {errors[`items.${index}.quantity`] && (
                        <div
                          style={{
                            color: "red",
                            fontSize: "0.8rem",
                            marginTop: "4px",
                          }}
                        >
                          {errors[`items.${index}.quantity`]}
                        </div>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <input
                        type="number"
                        style={{ ...inputStyle, textAlign: "center" }}
                        value={item.used_qty}
                        disabled
                      />
                    </TableCell>

                    <TableCell align="right">
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) =>
                          handleItemChange(index, "rate", e.target.value)
                        }
                        style={{
                          ...inputStyle,
                          textAlign: "right",
                          border: errors[`items.${index}.rate`]
                            ? "1px solid red"
                            : inputStyle.border,
                        }}
                      />
                      {errors[`items.${index}.rate`] && (
                        <div
                          style={{
                            color: "red",
                            fontSize: "0.8rem",
                            marginTop: "4px",
                          }}
                        >
                          {errors[`items.${index}.rate`]}
                        </div>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <input
                        type="number"
                        value={item.amount}
                        onChange={(e) =>
                          handleItemChange(index, "amount", e.target.value)
                        }
                        style={{ ...inputStyle, textAlign: "right" }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <input
                        type="text"
                        readOnly
                        value={(
                          parseFloat(item.rate || 0) *
                          parseFloat(item.quantity || 0)
                        ).toFixed(2)}
                        style={{ ...inputStyle, textAlign: "right" }}
                      />
                    </TableCell>

                    <TableCell>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) =>
                          handleItemChange(index, "description", e.target.value)
                        }
                        style={inputStyle}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <IconButton
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            items: prev.items.filter((_, i) => i !== index),
                          }))
                        }
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </TableContainer>
      )}

      {/* Grand Total */}
      {!isMobile && (
        <TableContainer>
          <Box sx={{ mb: 3, width: "100%" }}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell align="right">
                    <strong>Grand Total</strong>
                  </TableCell>
                  <TableCell align="left">
                    <TextField
                      fullWidth
                      value={totalValue.toFixed(2)}
                      size="small"
                      sx={{ width: "150px" }}
                      disabled
                    />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </TableContainer>
      )}

      {/* Terms & Conditions */}
      <Box
        border="1px solid #ccc"
        borderRadius="8px"
        sx={{ mb: 3, p: isMobile ? 1.5 : 2 }}
      >
        <Typography variant="h6" gutterBottom>
          Terms & Conditions
        </Typography>

        {terms.map((term, index) => (
          <Grid
            container
            spacing={1}
            key={index}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Grid item xs={5}>
              <TextField
                fullWidth
                placeholder="Term"
                value={term.key}
                onChange={(e) => handleTermChange(index, "key", e.target.value)}
                size="small"
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: isMobile ? "0.8rem" : "0.875rem",
                  },
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                placeholder="Value"
                value={term.value}
                onChange={(e) =>
                  handleTermChange(index, "value", e.target.value)
                }
                size="small"
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: isMobile ? "0.8rem" : "0.875rem",
                  },
                }}
              />
            </Grid>

            <Grid
              item
              xs={1}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <IconButton
                onClick={() => removeTerm(index)}
                color="error"
                size={isMobile ? "small" : "medium"}
              >
                <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Dialog
          open={openItemModal}
          onClose={handleCloseItemModal}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Add Item</DialogTitle>
          <DialogContent>
            <AddItemForm onClose={handleCloseItemModal} />
          </DialogContent>
        </Dialog>

        <Button
          variant="outlined"
          sx={{ mt: 1 }}
          onClick={() =>
            setTerms([...terms, { key: "", value: "", is_default: false }])
          }
        >
          + Add Term
        </Button>
      </Box>
    </Box>
  );
}
